/**
 * useRetellWebSocket Hook
 * Manages WebSocket connection for real-time events
 * Supports demo mode with mock data
 */

import { useEffect, useRef, useState } from 'react';
import { websocketService } from '@/services/websocket.service';
import { mockWebsocketService } from '@/services/mock-websocket.service';
import { useConversationStore } from '@/stores/conversationStore';
import { useUIStore } from '@/stores/uiStore';
import type {
  WebSocketEvent,
  CallStartedEvent,
  AgentMessageEvent,
  CircuitsFoundEvent,
  SearchStartedEvent,
  QuickRepliesEvent,
  QuickReply,
  UpdateVisualsEvent,
} from '@/types';

// Check if demo mode is enabled
const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';

/**
 * Hook to manage WebSocket connection and handle events
 */
export function useAgentWebSocket(callId: string | null) {
  const [isConnected, setIsConnected] = useState(false);

  // Safety timer: if a search yields no circuits, the worker sends no CIRCUITS_FOUND,
  // so the loading skeleton must be cleared after a timeout to avoid getting stuck.
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    setLastAgentMessage,
    setAvailableCircuits,
    setQuickReplies,
    setCurrentDestination,
    setCurrentSubtheme,
  } = useConversationStore();

  const { setBackgroundImages, setIsLoading } = useUIStore();

  useEffect(() => {
    if (!callId) {
      setIsConnected(false);
      return;
    }

    // Choose service based on mode
    const service = isDemoMode ? mockWebsocketService : websocketService;

    // Connect to WebSocket (real or mock)
    service.connect(callId, handleWebSocketMessage);
    setIsConnected(true);

    if (isDemoMode) {
      console.log('[useRetellWebSocket] 🎮 DEMO MODE - Using mock data');
    }

    // Cleanup on unmount
    return () => {
      service.disconnect();
      setIsConnected(false);
    };
  }, [callId]);

  /**
   * Handle incoming WebSocket messages
   */
  const handleWebSocketMessage = async (event: WebSocketEvent) => {
    console.log(`[${new Date().toLocaleTimeString()}] [useRetellWebSocket] Event received:`, event.type);

    switch (event.type) {
      case 'CALL_STARTED':
        await handleCallStarted(event as CallStartedEvent);
        break;

      case 'AGENT_MESSAGE':
        await handleAgentMessage(event as AgentMessageEvent);
        break;

      case 'SEARCH_STARTED':
        handleSearchStarted(event as SearchStartedEvent);
        break;

      case 'CIRCUITS_FOUND':
        handleCircuitsFound(event as CircuitsFoundEvent);
        break;

      case 'QUICK_REPLIES':
        handleQuickReplies(event as QuickRepliesEvent);
        break;

      case 'UPDATE_VISUALS':
        handleUpdateVisuals(event as UpdateVisualsEvent);
        break;

      case 'CALL_ENDED':
        console.log('[useRetellWebSocket] Call ended');
        stopSearchLoading();
        break;

      case 'ERROR':
        console.error('[useRetellWebSocket] Error:', event);
        break;

      default:
        console.warn('[useRetellWebSocket] Unknown event type:', event);
    }
  };

  /**
   * Handle call started event
   */
  const handleCallStarted = async (event: CallStartedEvent) => {
    console.log('[useRetellWebSocket] Call started:', event);

    if (event.destination) {
      setCurrentDestination(event.destination);
    }

    if (event.subtheme) {
      setCurrentSubtheme(event.subtheme);
    }
  };

  /**
   * Handle agent message event
   */
  const handleAgentMessage = async (event: AgentMessageEvent) => {
    console.log('[useRetellWebSocket] Agent message:', event.message);

    setLastAgentMessage(event.message);

    if (event.destination) {
      setCurrentDestination(event.destination);
    }

    if (event.subtheme) {
      setCurrentSubtheme(event.subtheme);
    }

    // Images provided by Worker (Smart Unsplash + Fallback)
    if (event.images && event.images.length > 0) {
      console.log('[useRetellWebSocket] Received images from Worker:', event.images.length);
      setBackgroundImages(event.images);
    }
  };

  const getDefaultQuickReplies = (): QuickReply[] => [
    { id: 'qr-1', text: 'Voir mes circuits favoris', icon: '❤️' },
    { id: 'qr-2', text: 'Prendre RDV', icon: '📞' },
    { id: 'qr-3', text: 'Nouvelle discussion', icon: '🗺️' },
  ];

  const normalizeQuickReplies = (replies: QuickReply[]): QuickReply[] => {
    const oldLabels = new Set([
      'Voir plus de détails',
      'Autres destinations',
      'Prendre contact',
      'Demander une autre destination à Anouk',
      'Changer de destination',
    ]);

    const hasOldLabels = replies.some((reply) => oldLabels.has(reply.text));
    const hasOldIds =
      replies.length === 3 &&
      replies.every((reply) => ['qr-1', 'qr-2', 'qr-3'].includes(reply.id)) &&
      replies.some((reply) => reply.text !== getDefaultQuickReplies().find((r) => r.id === reply.id)?.text);

    if (hasOldLabels || hasOldIds) {
      return getDefaultQuickReplies();
    }

    return replies;
  };

  /**
   * Handle circuits found event
   */
  /** Clear the search loading state and cancel the safety timer. */
  const stopSearchLoading = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setIsLoading(false);
  };

  /**
   * Handle search started event — show a loading skeleton while the circuit lookup
   * runs and the agent speaks, so the screen change never happens in silence.
   */
  const handleSearchStarted = (event: SearchStartedEvent) => {
    const dest = event.destination ? ` pour ${event.destination}` : '';
    console.log('[useRetellWebSocket] Search started:', event.destination);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    setIsLoading(true, `Je cherche les meilleurs circuits${dest}…`);
    // Failsafe: clear if no circuits come back (e.g. 0 results → no CIRCUITS_FOUND).
    searchTimeoutRef.current = setTimeout(() => setIsLoading(false), 15000);
  };

  const handleCircuitsFound = (event: CircuitsFoundEvent) => {
    console.log('[useRetellWebSocket] Circuits found:', event.circuits.length);

    stopSearchLoading();
    setAvailableCircuits(event.circuits);

    // Set quick replies if provided (worker sends qr-1, qr-2, qr-3)
    if (event.quickReplies && event.quickReplies.length > 0) {
      setQuickReplies(normalizeQuickReplies(event.quickReplies));
    } else {
      // Fallback: same three actions as worker
      setQuickReplies(getDefaultQuickReplies());
    }
  };

  /**
   * Handle update visuals event
   */
  const handleUpdateVisuals = (event: UpdateVisualsEvent) => {
    console.log('[useRetellWebSocket] Updating visuals from Worker:', event.images.length);
    if (event.images && event.images.length > 0) {
      setBackgroundImages(event.images);
    }
    if (event.destination) {
      setCurrentDestination(event.destination);
    }
  };

  /**
   * Handle quick replies event
   */
  const handleQuickReplies = (event: QuickRepliesEvent) => {
    console.log('[useRetellWebSocket] Quick replies:', event.replies.length);
    setQuickReplies(normalizeQuickReplies(event.replies));
  };

  /**
   * Send quick reply selection
   */
  const sendQuickReply = (replyText: string) => {
    if (!isConnected) {
      console.warn('[useRetellWebSocket] Cannot send reply - not connected');
      return;
    }

    // Choose service based on mode
    const service = isDemoMode ? mockWebsocketService : websocketService;

    service.send({
      type: 'USER_REPLY',
      message: replyText,
      timestamp: Date.now(),
    });

    console.log('[useRetellWebSocket] Quick reply sent:', replyText);
  };

  return {
    isConnected,
    sendQuickReply,
  };
}
